import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../sections/Footer";
import { projects } from "../data/portfolio";

/**
 * UniCreds CRM: flagship case study. Content mirrors the source brief:
 * Overview -> 16 numbered sections -> Next.
 */

const INK = "#0E1A2B";
const MAGENTA = "#E5326A";
const MAGENTA_SOFT = "#FDECF2";
const WHITE = "#FFFFFF";
const TINT = "#F7F7FA";
const MUTED = "#6E7583";
const LINE = "#E4E6EC";

const project = projects.find((p) => p.id === "unicreds-crm");

const bodyFont = { fontFamily: "'Inter', system-ui, sans-serif" };
const displayFont = {
  fontFamily: "'Manrope', 'Inter', system-ui, sans-serif",
  fontWeight: 800,
  letterSpacing: "-0.03em",
  lineHeight: 1.05,
};
const monoFont = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

const chapters = [
  { id: "ch-overview", n: "00", label: "Overview" },
  { id: "ch-context", n: "01", label: "The ecosystem" },
  { id: "ch-problem", n: "02", label: "The core problem" },
  { id: "ch-role", n: "03", label: "My role" },
  { id: "ch-hierarchy", n: "04", label: "A hierarchy of states" },
  { id: "ch-views", n: "05", label: "Two views, one application" },
  { id: "ch-workflow", n: "06", label: "Making it event-driven" },
  { id: "ch-tasks", n: "07", label: "Tasks that stay actionable" },
  { id: "ch-recovery", n: "08", label: "Rejection, withdrawal, recovery" },
  { id: "ch-duplicates", n: "09", label: "Protecting the source of truth" },
  { id: "ch-notifications", n: "10", label: "Notifications without fatigue" },
  { id: "ch-ownership", n: "11", label: "Ownership and permissions" },
  { id: "ch-model", n: "12", label: "The resulting product model" },
  { id: "ch-impact", n: "13", label: "Outcome" },
  { id: "ch-lessons", n: "14", label: "What I learned" },
  { id: "ch-future", n: "15", label: "Taking it further" },
  { id: "ch-takeaway", n: "16", label: "Final takeaway" },
];

/* ---------- Building blocks ---------- */

const Eyebrow = ({ children }) => (
  <div className="text-[11px] tracking-[0.22em] uppercase mb-6 font-medium" style={{ color: MUTED, ...bodyFont }}>
    {children}
  </div>
);

const SubHeading = ({ children }) => (
  <h3 className="mt-8 mb-3" style={{ ...displayFont, fontSize: 18, color: INK, fontWeight: 700 }}>
    {children}
  </h3>
);

const Blockquote = ({ children }) => (
  <blockquote
    className="my-8 border-l-2 pl-6 py-2 text-lg md:text-xl leading-[1.55] max-w-2xl"
    style={{ borderColor: MAGENTA, color: INK, ...bodyFont, fontWeight: 600 }}
  >
    “{children}”
  </blockquote>
);

const MetaRow = ({ role, team, product, focus }) => (
  <div className="my-10 border-y" style={{ borderColor: LINE }}>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-6">
      {[
        { k: "Role", v: role },
        { k: "Product", v: product },
        { k: "Team", v: team },
        { k: "Focus", v: focus },
      ].map((m) => (
        <div key={m.k}>
          <div className="text-[10px] tracking-[0.22em] uppercase mb-2" style={{ color: MUTED, ...bodyFont, fontWeight: 500 }}>{m.k}</div>
          <div className="text-sm" style={{ color: INK, ...bodyFont }}>{m.v}</div>
        </div>
      ))}
    </div>
  </div>
);

const ProductFrame = ({ src, alt, caption }) => (
  <figure className="my-8 md:my-10 max-w-2xl">
    <div className="rounded-2xl overflow-hidden border" style={{ borderColor: LINE, background: TINT }}>
      <img src={src} alt={alt} className="block w-full h-auto" loading="lazy" />
    </div>
    {caption && <figcaption className="mt-3 text-xs md:text-sm" style={{ color: MUTED, ...bodyFont }}>{caption}</figcaption>}
  </figure>
);

const YouCursor = () => (
  <div aria-hidden className="fixed left-8 bottom-14 z-40 flex items-center gap-2 pointer-events-none hidden md:flex">
    <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
      <path d="M2 2L18 12L10 14L6 22L2 2Z" fill={INK} />
    </svg>
    <span className="rounded-full px-3 py-1 text-xs font-medium shadow-lg" style={{ background: INK, color: WHITE, ...bodyFont }}>You</span>
  </div>
);

const LeftRail = ({ active, onJump }) => (
  <aside className="hidden lg:block sticky top-32 self-start max-h-[70vh] overflow-y-auto pr-2" aria-label="Case study chapters" data-testid="case-toc">
    <button
      onClick={() => (window.history.length > 1 ? window.history.back() : (window.location.href = "/#work"))}
      className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium mb-10"
      style={{ background: WHITE, color: INK, border: `1px solid ${LINE}`, ...bodyFont }}
      data-testid="case-back"
    >
      <ArrowLeft size={14} /> Back
    </button>
    <nav className="flex flex-col gap-1">
      {chapters.map((c) => {
        const isActive = c.id === active;
        return (
          <button
            key={c.id}
            onClick={() => onJump(c.id)}
            className="grid grid-cols-[28px_1fr] items-center gap-2 rounded-lg px-3 py-2 text-left transition"
            style={{
              background: isActive ? MAGENTA_SOFT : "transparent",
              color: isActive ? INK : MUTED,
              ...bodyFont,
              fontWeight: isActive ? 600 : 500,
            }}
            data-testid={`case-toc-${c.n}`}
          >
            <span style={{ ...monoFont, fontSize: 11, color: isActive ? MAGENTA : MUTED }}>{c.n}</span>
            <span className="text-[13px]">{c.label}</span>
          </button>
        );
      })}
    </nav>
  </aside>
);

const S = ({ id, children, noTopBorder }) => (
  <section id={id} data-testid={id} className={`scroll-mt-32 py-14 md:py-16 ${noTopBorder ? "" : "border-t"}`} style={{ borderColor: LINE }}>
    {children}
  </section>
);

const SectionHeading = ({ n, title }) => (
  <div className="flex items-baseline gap-4 mb-5">
    <span style={{ ...monoFont, color: MAGENTA, fontSize: 14, fontWeight: 500 }}>{n}</span>
    <h2 style={{ ...displayFont, fontSize: "clamp(1.5rem, 2.6vw, 2.3rem)", color: INK, lineHeight: 1.3 }}>{title}</h2>
  </div>
);

const P = ({ children, className = "" }) => (
  <p className={`text-base md:text-lg max-w-2xl ${className}`} style={{ color: INK, ...bodyFont, lineHeight: 1.7 }}>
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="mt-5 space-y-3 max-w-2xl">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 text-base" style={{ color: INK, ...bodyFont, lineHeight: 1.6 }}>
        <span style={{ ...monoFont, color: MAGENTA, fontSize: 12, paddingTop: 4 }}>{String(i + 1).padStart(2, "0")}</span>
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const StatusTable = ({ rows }) => (
  <div className="mt-6 rounded-2xl border overflow-hidden max-w-2xl" style={{ borderColor: LINE }}>
    <div className="grid grid-cols-[140px_1fr] gap-4 px-6 py-3" style={{ background: TINT }}>
      <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: MUTED, ...bodyFont, fontWeight: 600 }}>Simplified</span>
      <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: MUTED, ...bodyFont, fontWeight: 600 }}>Underlying statuses</span>
    </div>
    <div className="divide-y" style={{ borderColor: LINE }}>
      {rows.map((r) => (
        <div key={r.simplified} className="grid grid-cols-[140px_1fr] gap-4 px-6 py-4 items-baseline">
          <span
            className="text-xs font-semibold rounded-full px-3 py-1 inline-block w-fit"
            style={{ background: MAGENTA_SOFT, color: MAGENTA, ...bodyFont }}
          >
            {r.simplified}
          </span>
          <span className="text-sm" style={{ color: INK, ...bodyFont, lineHeight: 1.5 }}>{r.underlying}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ---------- Diagram primitives ---------- */

const Diagram = ({ children, className = "" }) => (
  <div className={`mt-8 rounded-2xl p-6 md:p-8 border flex flex-col items-center gap-3 max-w-2xl ${className}`} style={{ background: TINT, borderColor: LINE }}>
    {children}
  </div>
);

const DNode = ({ children, tone = "default" }) => (
  <div
    className="rounded-lg px-4 py-2.5 text-sm border bg-white text-center w-full"
    style={{
      borderColor: tone === "accent" ? MAGENTA : LINE,
      color: tone === "accent" ? MAGENTA : INK,
      ...bodyFont,
      fontWeight: 600,
    }}
  >
    {children}
  </div>
);

const DRoot = ({ children }) => (
  <div className="rounded-xl px-5 py-3 text-white text-sm font-semibold" style={{ background: INK, ...bodyFont }}>{children}</div>
);

const DLine = () => <div className="h-6 w-px" style={{ background: MUTED }} />;

const DRow = ({ children }) => (
  <div className={`grid gap-3 w-full`} style={{ gridTemplateColumns: `repeat(${React.Children.count(children)}, 1fr)` }}>
    {children}
  </div>
);

/* ---------- Root ---------- */

export const UnicredsCaseStudy = () => {
  const [active, setActive] = useState("ch-overview");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    chapters.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!project) return null;

  const idx = projects.findIndex((p) => p.id === "unicreds-crm");
  const next = projects[(idx + 1) % projects.length];

  return (
    <div style={{ background: WHITE, minHeight: "100vh", color: INK, ...bodyFont }} data-testid="case-study-unicreds-crm">
      <Navigation />
      <YouCursor />
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 pt-28">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-3">
            <LeftRail active={active} onJump={jumpTo} />
          </div>
          <main className="col-span-12 lg:col-span-9 max-w-3xl">
            {/* Header */}
            <section className="pb-2">
              <Eyebrow>UniCreds · Multi-Party Lending CRM</Eyebrow>
              <h1 style={{ ...displayFont, fontSize: "clamp(2rem, 4vw, 3.8rem)", color: INK, lineHeight: 1.15 }} data-testid="case-title">
                Redesigning UniCreds' Loan CRM for a Multi-Party Lending Workflow
              </h1>
              <p className="mt-4 text-base md:text-lg max-w-2xl" style={{ color: MUTED, ...bodyFont }}>
                How I simplified lender operations, status visibility, task management, and internal coordination across an education-loan marketplace.
              </p>
              <MetaRow
                role="UI/UX Designer"
                product="UniCreds CRM"
                team="UniCreds · Lenders · Partners · Engineering"
                focus="Status systems · Workflow design · RBAC · Notifications"
              />
            </section>

            {/* Overview */}
            <S id="ch-overview" noTopBorder>
              <Eyebrow>Overview</Eyebrow>
              <P>
                UniCreds helps students compare and process education loans while coordinating with multiple lending partners. A single application moves through two different working environments: the internal UniCreds CRM and the lender-facing portal. That's a bigger problem than UI cleanup. The same application means different things to different users, status vocabulary is too granular for some stakeholders and too vague for others, and a closed application can leave stale work behind if nobody cleans up after it.
              </P>
              <SubHeading>Four ideas shaped the redesign</SubHeading>
              <BulletList
                items={[
                  "One underlying application journey, multiple role-appropriate views.",
                  "A simplified status layer for visibility, while keeping granular operational statuses underneath.",
                  "System-driven transitions and notifications wherever the next action is deterministic.",
                  "Explicit handling of edge cases, so closed or duplicate applications don't stay operationally \"alive.\"",
                ]}
              />
              <P className="mt-6">
                The work grew from journey mapping and Figma redesigns into lender-side workflow, notification and task-management improvements. The lender CRM now runs live across <strong style={{ color: MAGENTA }}>20+ lenders</strong>.
              </P>
            </S>

            {/* 01 */}
            <S id="ch-context">
              <SectionHeading n="01" title="The ecosystem" />
              <P>
                UniCreds operates as an education-loan marketplace. A student doesn't submit a form and get a binary yes or no, the journey runs through qualification, documentation, lender selection, application login, sanction, processing-fee confirmation and disbursal. And the same journey is worked by different organisations at once: students provide information, UniCreds teams qualify the lead and coordinate applications, lender users work their own applications inside their own context, and partners originate leads and need progress visibility without internal CRM complexity.
              </P>
              <P className="mt-4">
                The CRM had to behave less like a conventional sales pipeline and more like a <strong>workflow orchestration layer</strong> between multiple parties.
              </P>
              <Diagram>
                <DRoot>Student</DRoot>
                <DLine />
                <DNode tone="accent">UniCreds CRM</DNode>
                <DLine />
                <DRow>
                  <DNode>Lender Portal · Executive / Manager / Regional</DNode>
                  <DNode>Partner · progress visibility only</DNode>
                </DRow>
                <DLine />
                <DRow>
                  <DNode>Login Agent</DNode>
                  <DNode>Sanction Agent</DNode>
                  <DNode>Assistant Manager</DNode>
                </DRow>
              </Diagram>
            </S>

            {/* 02 */}
            <S id="ch-problem">
              <SectionHeading n="02" title="The core problem" />
              <P>
                At first glance this looked like a CRM redesign. The deeper problem was state coordination. A lead could be active inside UniCreds while one or more lender applications existed underneath it. Lenders needed enough granularity to work a case; internal teams and partners just needed to know where an application stood and what happens next.
              </P>

              <SubHeading>Statuses described system state, not user intent</SubHeading>
              <P>
                Operational statuses such as Under Discussion, Not Responding, Awaiting Docs and Future Date are useful to whoever's working the case. They're far less useful to a stakeholder who just needs to know an application is in a follow-up phase. Showing every backend status everywhere raised cognitive load; hiding the detail entirely made the CRM unusable for operators. The product needed both.
              </P>

              <SubHeading>Two working surfaces created coordination gaps</SubHeading>
              <P>
                UniCreds agents and lender users weren't doing the same job. A lender executive needed task-level follow-ups and application-specific notes. A UniCreds user needed an aggregated view across the student's journey, and potentially, multiple lender applications. Treating both as if they needed the same screen structure created information overload on one side and missing context on the other.
              </P>

              <SubHeading>Manual follow-up created stale work</SubHeading>
              <P>
                When an application was rejected or withdrawn, the business journey was effectively closed. But open lender tasks could remain in pending queues even though there was no valid next action, work that was technically incomplete but operationally meaningless.
              </P>

              <SubHeading>Edge cases at scale</SubHeading>
              <P>Several "exceptions" materially affected data and workflow quality:</P>
              <BulletList
                items={[
                  "A duplicate application already exists.",
                  "A lender has no matching product, or the student isn't eligible.",
                  "The application is already processed elsewhere.",
                  "The student requests withdrawal.",
                  "The assigned lender executive is no longer available.",
                  "An application needs to be revived before login.",
                  "A lead returns into the ecosystem after previously being marked lost.",
                ]}
              />
            </S>

            {/* 03 */}
            <S id="ch-role">
              <SectionHeading n="03" title="My role" />
              <P>I worked across product discovery, workflow design, UX, and requirement definition.</P>
              <BulletList
                items={[
                  "Mapped the existing UniCreds and lender-side journeys.",
                  "Identified gaps through user and workflow research.",
                  "Defined information architecture for role-specific CRM views.",
                  "Rationalised lender statuses into a simpler shared status model.",
                  "Designed wireframes and flows in Figma.",
                  "Defined backend mappings, triggers, task behaviour, and edge cases.",
                  "Worked with engineering and business teams on implementation and iteration.",
                  "Extended the model into partner visibility and operational controls.",
                ]}
              />
            </S>

            {/* 04 */}
            <S id="ch-hierarchy">
              <SectionHeading n="04" title="A hierarchy of states" />
              <P>
                One of the most important decisions was separating granular working statuses from simplified application states. Instead of forcing every user to understand the lowest-level state, I treated the status model as a hierarchy.
              </P>
              <StatusTable
                rows={[
                  { simplified: "New Leads", underlying: "Yet to Connect" },
                  { simplified: "Follow Ups", underlying: "Under Discussion · Not Responding · Awaiting Docs · Future Date" },
                  { simplified: "Ready to Login", underlying: "Ready to Login" },
                  { simplified: "Logged In", underlying: "Logged In" },
                  { simplified: "Sanctioned", underlying: "Sanctioned" },
                  { simplified: "PF Paid", underlying: "PF Paid" },
                  { simplified: "Disbursed", underlying: "Disbursal" },
                  { simplified: "Lost", underlying: "Rejected · Duplicate · No Product / Not Eligible · Already Processed" },
                ]}
              />
              <P className="mt-6">
                Granular statuses stayed available where an operator needed to decide the next action. Simplified statuses could be used for summary views, reporting, partner visibility, and cross-system communication.
              </P>
              <Blockquote>
                Operator: what exactly do I need to do next? Stakeholder: what stage is this application in?
              </Blockquote>
              <P>That distinction became a design principle for the rest of the CRM.</P>
            </S>

            {/* 05 */}
            <S id="ch-views">
              <SectionHeading n="05" title="Two views, one application" />
              <P>
                The CRM effectively had two fronts: an internal UniCreds view and a lender-side view. Rather than mirror the entire database on both surfaces, I defined the information around each user's job.
              </P>

              <SubHeading>UniCreds view: orchestration</SubHeading>
              <P>The UniCreds user needs to understand the student holistically:</P>
              <BulletList
                items={[
                  "Student and admission context.",
                  "Requested loan details.",
                  "Documents and readiness.",
                  "Lenders the student has been forwarded to.",
                  "Status of each lender application.",
                  "Sanction, PF and disbursal milestones.",
                  "Internal ownership and follow-up.",
                  "Notes and history.",
                ]}
              />
              <P className="mt-6">
                A student can have multiple lender applications, so the primary object from the UniCreds perspective is the student lead, with loan applications nested underneath it.
              </P>
              <ProductFrame
                src="/case-studies/unicreds/lead-details.webp"
                alt="UniCreds Lead Details orchestration view"
                caption="The Lead Details page: student context, applications, documents, tasks and notes in one operational view."
              />
              <ProductFrame
                src="/case-studies/unicreds/multiple-lenders.webp"
                alt="Multiple lender applications for one student lead"
                caption="A student's applications stacked across lenders, each carrying its own status and timeline."
              />

              <SubHeading>Documents as a readiness gate</SubHeading>
              <P>
                Documents weren't just files stored against a lead, they determined whether the application could move forward. Rather than a binary uploaded/not-uploaded flag, the system tracks readiness as Not Ready, Partial, or Ready, and only forwards an application to a lender once it qualifies.
              </P>
              <ProductFrame
                src="/case-studies/unicreds/readiness.png"
                alt="Document readiness states gating lender forwarding"
                caption="Readiness as a gate: Not Ready, Partial and Ready, with forwarding enabled only once a lead qualifies."
              />
              <ProductFrame
                src="/case-studies/unicreds/documents.webp"
                alt="Document management across a loan application"
                caption="Documents managed against the application, feeding directly into the readiness state above."
              />

              <SubHeading>Lender view: execution</SubHeading>
              <P>The lender user works a specific application. Their questions are different:</P>
              <BulletList
                items={[
                  "Which applications need action?",
                  "What is the latest context?",
                  "What task is due?",
                  "What document or student response am I waiting for?",
                  "What status should I move the case to?",
                ]}
              />
              <P className="mt-6">
                For the lender, the application, not the overall UniCreds lead, is the main unit of work.
              </P>
              <Diagram>
                <DRoot>Student Lead</DRoot>
                <DLine />
                <DNode>UniCreds lead view</DNode>
                <DLine />
                <DRow>
                  <DNode>Application · Lender A</DNode>
                  <DNode>Application · Lender B</DNode>
                  <DNode>Application · Lender C</DNode>
                </DRow>
                <DLine />
                <DNode tone="accent">Lender work queues</DNode>
              </Diagram>
            </S>

            {/* 06 */}
            <S id="ch-workflow">
              <SectionHeading n="06" title="Making it event-driven" />
              <P>
                Status design alone wouldn't solve coordination. The next question was: when the system already knows what should happen next, why require another user to manually coordinate it?
              </P>
              <SubHeading>Status updates as triggers</SubHeading>
              <P>A status transition could drive downstream actions such as:</P>
              <BulletList
                items={[
                  "Updating the mapped application state.",
                  "Recording a system event in history.",
                  "Notifying the relevant user.",
                  "Surfacing a milestone to another stakeholder.",
                  "Clearing work that's no longer actionable.",
                ]}
              />
              <Diagram>
                <DNode>Lender updates status</DNode>
                <DLine />
                <DRow>
                  <DNode>Persist granular status</DNode>
                  <DNode tone="accent">Map to simplified status</DNode>
                </DRow>
                <DLine />
                <DRow>
                  <DNode>Sync to UniCreds timeline</DNode>
                  <DNode>Trigger notification</DNode>
                </DRow>
              </Diagram>
              <P className="mt-6">
                The goal wasn't to automate every interaction. It was to automate deterministic coordination, so people could focus on decisions and conversations.
              </P>
            </S>

            {/* 07 */}
            <S id="ch-tasks">
              <SectionHeading n="07" title="Tasks that stay actionable" />
              <P>
                Lender users use tasks to manage follow-ups. A task is useful while an application is active; it becomes noise once the application is closed.
              </P>
              <SubHeading>The stale-task problem</SubHeading>
              <P>If an application became Rejected or Withdrawn, open tasks linked to it could remain incomplete:</P>
              <BulletList
                items={[
                  "Pending-task counts no longer represented real workload.",
                  "Lender users had to manually clean up work that no longer mattered.",
                  "Managers couldn't trust the task queue as an operational view.",
                ]}
              />
              <SubHeading>The rule</SubHeading>
              <P>
                When an application transitions to Rejected or Withdrawn, every incomplete task tied to that specific application is completed automatically. The rule applies across lender roles, preserves task history, and leaves other applications for the same student untouched.
              </P>
              <Diagram>
                <DNode>Application status changes</DNode>
                <DLine />
                <DNode tone="accent">Rejected or Withdrawn?</DNode>
                <DLine />
                <DRow>
                  <DNode>Complete open tasks for this application</DNode>
                  <DNode>Other applications stay untouched</DNode>
                </DRow>
                <DLine />
                <DNode>Removed from pending queue · kept in history</DNode>
              </Diagram>
              <P className="mt-6">
                The automation is scoped to the loan application, not the student lead. If the student has another active application with another lender, its tasks remain open, a small guardrail that stops one terminal lender decision from accidentally closing valid work elsewhere in the marketplace.
              </P>
              <ProductFrame
                src="/case-studies/unicreds/tasks.jpg"
                alt="Lender task queue tied to a specific loan application"
                caption="The lender task queue: follow-ups tied to a specific application, cleared automatically once that application closes."
              />
            </S>

            {/* 08 */}
            <S id="ch-recovery">
              <SectionHeading n="08" title="Rejection, withdrawal, recovery" />
              <SubHeading>Lost is a category, not a reason</SubHeading>
              <P>
                The simplified Lost state groups multiple business outcomes: Rejected, Duplicate, No Product / Not Eligible, Already Processed. The underlying reason still matters, each outcome implies a different business interpretation even once the application is no longer actionable.
              </P>
              <SubHeading>Withdrawn isn't the same as rejected</SubHeading>
              <P>
                A rejection is a lender decision. A withdrawal is an operational decision to stop that application, for example when the student asks to withdraw, the assigned lender executive becomes unavailable or leaves the organisation, or another pre-login change requires the application to restart. Treating withdrawal as permanently irreversible forced teams to create a new application for recoverable cases, introducing duplicate records.
              </P>
              <SubHeading>Revival before login</SubHeading>
              <P>The improved flow allows a withdrawn application to be revived before the Logged In stage. On revival:</P>
              <BulletList
                items={[
                  "The same application ID is retained.",
                  "The application restarts from Yet to Connect.",
                  "A different lender manager or executive is assigned.",
                  "The previously assigned executive is excluded from reassignment.",
                  "The revival is recorded in the application timeline.",
                ]}
              />
              <Diagram>
                <DRow>
                  <DNode>Yet to Connect</DNode>
                  <DNode>Follow Up</DNode>
                  <DNode>Ready to Login</DNode>
                  <DNode>Logged In</DNode>
                </DRow>
                <DLine />
                <DNode tone="accent">Withdrawn → revive before Login, retain ID, reassign owner</DNode>
              </Diagram>
            </S>

            {/* 09 */}
            <S id="ch-duplicates">
              <SectionHeading n="09" title="Protecting the source of truth" />
              <P>
                Duplicates are especially damaging in a marketplace CRM: they create false workload, split notes across records, and distort attribution.
              </P>
              <Blockquote>
                If an active record already represents the real student or application context, new duplicate activity should enrich the active record rather than create a second operational truth.
              </Blockquote>
              <P>
                For example, when duplicate lead context is uploaded with a comment, the note is attached to the active lead rather than left on a repeated record nobody will work. The same principle carries into cross-system flows where a student leaves UniCreds, progresses elsewhere in the ecosystem, and returns again: duplicate detection couldn't be a simple "same phone number, reject" rule, it had to understand whether the existing record was active, terminal, or specifically awaiting renewal or reactivation.
              </P>
              <Diagram>
                <DNode>Incoming lead or application</DNode>
                <DLine />
                <DNode tone="accent">Matching active record?</DNode>
                <DLine />
                <DRow>
                  <DNode>No: create new record</DNode>
                  <DNode>Valid renewal: reactivate</DNode>
                  <DNode>Repeat: mark duplicate, preserve context</DNode>
                </DRow>
              </Diagram>
            </S>

            {/* 10 */}
            <S id="ch-notifications">
              <SectionHeading n="10" title="Notifications without fatigue" />
              <P>
                Visibility and notifications aren't the same feature. A note or milestone may need to be visible in history without deserving an email or Slack interruption, a distinction that mattered more as the CRM expanded to partners and additional internal teams.
              </P>
              <Blockquote>
                A notification should exist only when it changes someone's likely next action.
              </Blockquote>
              <P>
                For partner-visible notes, email is restricted to meaningful active stages such as Contacted, Docs Pending, Logged In, Sanctioned and PF Confirmed. Other notes stay visible in the portal without generating an email, and system-generated milestones improve transparency without turning every transition into a manual message.
              </P>
              <Diagram>
                <DNode>CRM event or note</DNode>
                <DLine />
                <DRow>
                  <DNode>Always logged in the timeline</DNode>
                  <DNode tone="accent">Notification only if the next action changes</DNode>
                </DRow>
              </Diagram>
              <ProductFrame
                src="/case-studies/unicreds/timeline.webp"
                alt="Timeline keeping every CRM event visible"
                caption="Visibility is persistent, notifications are selective: the timeline keeps every event, emails go out only when the next action actually changes."
              />
            </S>

            {/* 11 */}
            <S id="ch-ownership">
              <SectionHeading n="11" title="Ownership and permissions" />
              <P>
                The CRM also needed to represent who owns what without letting users accidentally break routing logic, a rule that became especially visible when extending the system to partnership users.
              </P>
              <P className="mt-4">
                A partner maps to one active UniCreds Partnership Executive at a time, while one executive can manage multiple partners. The mapping is enforced at the backend and every change is logged.
              </P>
              <Blockquote>
                Access and ownership should be derived from role and mapping, not from a user remembering the right filter.
              </Blockquote>
              <Diagram>
                <DRoot>Partnership Executive</DRoot>
                <DLine />
                <DNode>Partners (many)</DNode>
                <DLine />
                <DNode>Leads</DNode>
                <DLine />
                <DNode>Loan Applications</DNode>
                <DLine />
                <DNode tone="accent">Lenders</DNode>
              </Diagram>
              <P className="mt-6">
                The same principle applies more broadly across the CRM: role-specific permissions elsewhere also stop users from performing actions outside their responsibility or skipping required workflow stages.
              </P>
            </S>

            {/* 12 */}
            <S id="ch-model">
              <SectionHeading n="12" title="The resulting product model" />
              <P>The final model can be summarised as five connected layers.</P>
              <Diagram>
                <DRoot>Student Lead</DRoot>
                <DLine />
                <DNode>Loan Applications</DNode>
                <DLine />
                <DNode>Granular Operational Status</DNode>
                <DLine />
                <DRow>
                  <DNode tone="accent">Simplified Shared Status</DNode>
                  <DNode tone="accent">Tasks · Notes · Notifications · Ownership</DNode>
                </DRow>
              </Diagram>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8 max-w-2xl">
                {[
                  { t: "Layer 1 · Student lead", b: "The single customer-level record used by UniCreds." },
                  { t: "Layer 2 · Loan applications", b: "One student can have multiple lender-specific applications." },
                  { t: "Layer 3 · Granular status", b: "Enough detail for the user actively processing the case." },
                  { t: "Layer 4 · Simplified status", b: "A stable vocabulary for cross-system visibility and reporting." },
                  { t: "Layer 5 · Workflow behaviour", b: "Tasks, notes, notifications, role ownership, audit history and system automations attached to state changes." },
                ].map((p, i) => (
                  <div key={p.t} className="rounded-xl p-5 border" style={{ background: WHITE, borderColor: LINE }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ ...monoFont, color: MAGENTA, fontSize: 12 }}>{String(i + 1).padStart(2, "0")}</span>
                      <span style={{ ...displayFont, fontSize: 15, color: INK, fontWeight: 700 }}>{p.t}</span>
                    </div>
                    <p className="text-sm" style={{ color: MUTED, ...bodyFont, lineHeight: 1.55 }}>{p.b}</p>
                  </div>
                ))}
              </div>
              <P className="mt-8">
                This architecture stopped the CRM from becoming a flat list of fields and buttons. Instead, it established a clear relationship between state, ownership, visibility and action.
              </P>
            </S>

            {/* Impact */}
            <S id="ch-impact">
              <Eyebrow>Outcome</Eyebrow>
              <h2 style={{ ...displayFont, fontSize: "clamp(1.8rem, 3.2vw, 3.2rem)", color: INK, lineHeight: 1.35 }}>
                Adoption was the real signal, not a <span style={{ color: MAGENTA }}>redesign checklist</span>.
              </h2>
              <P className="mt-6">
                The CRM work moved beyond a one-time interface redesign and became part of the operating workflow between UniCreds and its lending partners. The clearest adoption outcome: the lender CRM now runs across <strong style={{ color: MAGENTA }}>20+ lenders</strong>, backed by improvements to lender-side workflows, notifications and task management.
              </P>
              <BulletList
                items={[
                  "A clearer shared language for application progress.",
                  "A real separation between student-level and lender-application-level context.",
                  "More reliable task queues.",
                  "Less manual coordination for deterministic state changes.",
                  "Explicit handling of rejected, withdrawn, duplicate and ineligible applications.",
                  "Better visibility across internal and lender teams.",
                  "A stronger foundation for partner-facing visibility and permissions built later.",
                ]}
              />
              <P className="mt-6">
                I'd rather claim workflow adoption and operational integrity here than a conversion number I can't cleanly attribute to this redesign.
              </P>
            </S>

            {/* What I learned */}
            <S id="ch-lessons">
              <Eyebrow>What I Learned</Eyebrow>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 max-w-2xl">
                {[
                  { t: "CRM UX is mostly state design", b: "It's tempting to treat an internal tool as a screen-density problem. The hardest questions were about state: what it means, who can change it, what happens automatically, and whether the application can come back from it." },
                  { t: "One truth doesn't mean one view", b: "Lender users, internal teams and partners shouldn't see identical representations of the same application. The better model was a shared underlying state with role-appropriate abstraction on top." },
                  { t: "Terminal states need design too", b: "Rejected, duplicate, withdrawn and ineligible records get less attention because the successful journey is more exciting, but they affect task queues, reporting, reactivation and trust." },
                  { t: "Automation earns trust at hand-offs", b: "The highest-value automations weren't flashy. They were the small rules that stopped one team waiting for another to manually communicate something the system already knew." },
                  { t: "Edge cases reveal the real data model", b: "Duplicate leads, multiple lender applications, withdrawn revival, and cross-system re-entry forced us to define the real primary object, and when a record should be reused, renewed or closed." },
                ].map((p) => (
                  <div key={p.t} className="rounded-xl p-5 border" style={{ background: TINT, borderColor: LINE }}>
                    <div style={{ ...displayFont, fontSize: 15, color: INK, fontWeight: 700 }} className="mb-2">{p.t}</div>
                    <p className="text-sm" style={{ color: MUTED, ...bodyFont, lineHeight: 1.55 }}>{p.b}</p>
                  </div>
                ))}
              </div>
            </S>

            {/* Taking it further */}
            <S id="ch-future">
              <Eyebrow>If I Took This Further</Eyebrow>
              <P>
                The next iteration would focus on making the workflow more measurable, not simply adding more CRM controls. I'd instrument:
              </P>
              <BulletList
                items={[
                  "Time spent in each lender status.",
                  "Task ageing by application stage.",
                  "Lender-wise status transition time.",
                  "Applications requiring manual UniCreds intervention.",
                  "Withdrawal → revival rate.",
                  "Duplicate / repeated lead rate by source.",
                  "Share of status transitions generated automatically vs manually.",
                  "Notification-to-action rate.",
                  "Drop-off between Ready to Login → Logged In → Sanctioned → PF Paid → Disbursed.",
                ]}
              />
              <P className="mt-6">
                That would turn the CRM from an operational system into a stronger workflow intelligence layer, making bottlenecks visible by lender, stage and ownership instead of relying on anecdotal escalation.
              </P>
            </S>

            {/* Final takeaway */}
            <S id="ch-takeaway">
              <Eyebrow>Final Takeaway</Eyebrow>
              <P>The most useful change in this project wasn't a single screen. It was changing the mental model from:</P>
              <Blockquote>A CRM is a place where users update statuses.</Blockquote>
              <P>into:</P>
              <Blockquote>A CRM is a system that coordinates state, ownership, and next actions across multiple organisations.</Blockquote>
              <P className="mt-4">
                For a multi-lender education-loan journey, that distinction mattered. The system had to stay detailed enough for the person processing an application, simple enough for everyone else to understand, and strict enough that closed, duplicated or reassigned work didn't silently corrupt the operating queue. That became the foundation for the UniCreds CRM redesign and the lender and partner workflow improvements built on top of it.
              </P>
            </S>

            {/* Next Step */}
            <S id="ch-next">
              <Eyebrow>Next Step</Eyebrow>
              <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
                <div>
                  <div className="text-sm mb-2" style={{ color: MUTED, ...bodyFont }}>Continue reading</div>
                  <div style={{ ...displayFont, fontSize: "clamp(1.8rem, 3.6vw, 3.6rem)", color: INK }}>{next.title}</div>
                  <div className="mt-2 text-xs tracking-[0.22em] uppercase" style={{ color: MUTED, ...bodyFont }}>{next.category}</div>
                </div>
                <Link
                  to={`/work/${next.id}`}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
                  style={{ background: INK, color: WHITE, ...bodyFont }}
                  data-testid="case-next-link"
                >
                  Open case study <ArrowUpRight size={14} />
                </Link>
              </div>
            </S>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default UnicredsCaseStudy;
